/* Letterman navigation and form-feedback behavior. No tracking or submission logic. */
(function () {
  'use strict';

  if (window.lettermanRuntimeAccessibility) {
    window.lettermanRuntimeAccessibility.destroy();
  }
  var globalEvents = new AbortController();
  var headers = new Map();
  var focusedFeedback = new WeakSet();
  var breakpoint = window.matchMedia('(max-width: 1080px)');
  var pendingFrame = 0;

  function focusElement(element) {
    if (!element || !element.isConnected) return;
    try { element.focus({ preventScroll: true }); }
    catch (error) { element.focus(); }
  }

  function firstControl(container) {
    return container && Array.from(container.querySelectorAll('a[href],button,summary,[tabindex="0"]'))
      .find(function (element) { return element.getClientRects().length && !element.disabled; });
  }

  function measureHeaders() {
    pendingFrame = 0;
    var stickyHeight = 0;
    headers.forEach(function (state, root) {
      if (!root.isConnected) return;
      var inner = root.querySelector('.ls-header__inner');
      if (!inner) return;
      var rect = inner.getBoundingClientRect();
      var border = parseFloat(getComputedStyle(root).borderBottomWidth) || 0;
      var wrapper = root.closest('.shopify-section') || root.parentElement;
      if (root.dataset.lsSticky === 'true' && wrapper && getComputedStyle(wrapper).position === 'sticky') {
        stickyHeight = Math.max(stickyHeight, Math.ceil(rect.height + border));
      }
      root.style.setProperty('--ls-menu-max-height', Math.max(48, window.innerHeight - Math.max(0, rect.bottom + border) - 8) + 'px');
    });
    document.documentElement.style.setProperty('--ls-sticky-header-height', stickyHeight + 'px');
  }

  function queueMeasurement() {
    if (!pendingFrame) pendingFrame = window.requestAnimationFrame(measureHeaders);
  }

  function initializeHeader(root) {
    if (headers.has(root)) return;
    var toggle = root.querySelector('[data-ls-menu-toggle]');
    var menu = root.querySelector('[data-ls-mobile-menu]');
    if (!toggle || !menu) return;
    var events = new AbortController();
    var signal = events.signal;
    var desktopNav = root.querySelector('.ls-header__nav');
    var dropdowns = Array.from(root.querySelectorAll('[data-ls-nav-details],.ls-mobile-dropdown'));
    var label = toggle.querySelector('.ls-sr-only');
    var mode = breakpoint.matches;
    var lastNavigationFocus = null;
    var focusFrame = 0;

    function navigationSide(element) {
      if (!element) return null;
      if (element === toggle || menu.contains(element)) return 'mobile';
      return desktopNav && desktopNav.contains(element) ? 'desktop' : null;
    }
    function defaultFocus(element) {
      return !element || element === document.body || element === document.documentElement;
    }
    function visibleControl(element) {
      return Boolean(element && element.isConnected && element.getClientRects().length &&
        getComputedStyle(element).visibility !== 'hidden');
    }
    function cancelFocusTransfer(forget) {
      if (focusFrame) window.cancelAnimationFrame(focusFrame);
      focusFrame = 0;
      if (forget) lastNavigationFocus = null;
    }

    function mobileOpen() { return toggle.getAttribute('aria-expanded') === 'true' && !menu.hidden; }
    function closeDropdowns(except) {
      dropdowns.forEach(function (item) { if (item !== except) item.open = false; });
    }
    function setMobileOpen(open, restoreFocus) {
      open = Boolean(open && breakpoint.matches);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (label) label.textContent = open ? 'Close menu' : 'Open menu';
      menu.hidden = !open;
      root.toggleAttribute('data-ls-mobile-open', open);
      if (!open) closeDropdowns();
      if (restoreFocus && breakpoint.matches) focusElement(toggle);
      queueMeasurement();
    }
    function onModeChange() {
      if (mode === breakpoint.matches) { queueMeasurement(); return; }
      var active = document.activeElement;
      var origin = navigationSide(active);
      var previousSide = mode ? 'mobile' : 'desktop';
      // A browser may clear activeElement as soon as CSS hides the old navigation.
      // Use only the last actual focus in that now-hidden navigation, never a visit.
      if (!origin && defaultFocus(active) && lastNavigationFocus &&
          lastNavigationFocus.side === previousSide && lastNavigationFocus.element.isConnected &&
          !visibleControl(lastNavigationFocus.element)) {
        origin = lastNavigationFocus.side;
      }
      cancelFocusTransfer(false);
      mode = breakpoint.matches;
      setMobileOpen(false, false);
      closeDropdowns();
      if (origin !== previousSide) { lastNavigationFocus = null; return; }
      var destinationMode = mode;
      focusFrame = window.requestAnimationFrame(function () {
        focusFrame = 0;
        if (signal.aborted || !root.isConnected || breakpoint.matches !== destinationMode) return;
        var current = document.activeElement;
        // An intentional focus move elsewhere always wins over resize recovery.
        if (!defaultFocus(current) && visibleControl(current)) return;
        var destination = destinationMode ? toggle :
          firstControl(desktopNav) || root.querySelector('.ls-header__logo');
        if (visibleControl(destination)) focusElement(destination);
      });
    }
    toggle.addEventListener('click', function () { setMobileOpen(!mobileOpen(), false); }, { signal: signal });
    dropdowns.forEach(function (item) {
      item.addEventListener('toggle', function () { if (item.open) closeDropdowns(item); }, { signal: signal });
    });
    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var target = event.target instanceof Element ? event.target : null;
      var dropdown = target && target.closest('details[open]');
      if (dropdown && dropdowns.indexOf(dropdown) !== -1) {
        event.preventDefault();
        dropdown.open = false;
        focusElement(dropdown.querySelector('summary'));
      } else if (mobileOpen()) {
        event.preventDefault();
        setMobileOpen(false, true);
      }
    }, { signal: signal });
    document.addEventListener('focusin', function (event) {
      var side = navigationSide(event.target);
      var hidingFocusedNavigation = defaultFocus(event.target) && mode !== breakpoint.matches &&
        lastNavigationFocus && !visibleControl(lastNavigationFocus.element);
      if (side) {
        cancelFocusTransfer(false);
        lastNavigationFocus = { element: event.target, side: side };
      } else if (!hidingFocusedNavigation) {
        cancelFocusTransfer(true);
      }
      if (!root.contains(event.target)) setMobileOpen(false, false);
      dropdowns.forEach(function (item) { if (item.open && !item.contains(event.target)) item.open = false; });
    }, { signal: signal });
    document.addEventListener('focusout', function (event) {
      if (!lastNavigationFocus || event.target !== lastNavigationFocus.element) return;
      // A normal blur or Tab to content must not leave stale navigation intent.
      if (!defaultFocus(event.relatedTarget) ||
          (mode === breakpoint.matches && !focusFrame) || visibleControl(event.target)) {
        cancelFocusTransfer(true);
      }
    }, { signal: signal });
    document.addEventListener('pointerdown', function (event) {
      cancelFocusTransfer(!navigationSide(event.target));
    }, { signal: signal });
    document.addEventListener('keydown', function (event) {
      if (focusFrame || !root.contains(event.target)) cancelFocusTransfer(true);
    }, { capture: true, signal: signal });
    window.addEventListener('blur', function () { cancelFocusTransfer(true); }, { signal: signal });
    breakpoint.addEventListener('change', onModeChange, { signal: signal });
    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) { setMobileOpen(false, false); closeDropdowns(); }
    }, { signal: signal });
    root.addEventListener('click', function (event) {
      var target = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!target || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || target.target === '_blank') return;
      var inMenu = menu.contains(target);
      var dropdown = target.closest('details');
      if (inMenu) setMobileOpen(false, true);
      else if (dropdown) { dropdown.open = false; focusElement(dropdown.querySelector('summary')); }
      else if (mobileOpen()) setMobileOpen(false, false);
      // Keep native navigation. For a same-document destination, move focus out of the closed panel.
      var url;
      try { url = new URL(target.href, location.href); } catch (error) { return; }
      if ((inMenu || dropdown) && url.origin === location.origin && url.pathname === location.pathname && url.search === location.search && url.hash) {
        var id;
        try { id = decodeURIComponent(url.hash.slice(1)); } catch (error) { return; }
        window.requestAnimationFrame(function () {
          var destination = document.getElementById(id);
          if (!destination) return;
          var addedTabindex = !destination.hasAttribute('tabindex');
          if (addedTabindex) destination.setAttribute('tabindex', '-1');
          focusElement(destination);
          if (addedTabindex) destination.addEventListener('blur', function () { destination.removeAttribute('tabindex'); }, { once: true });
        });
      }
    }, { signal: signal });
    var resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(queueMeasurement) : null;
    if (resizeObserver) resizeObserver.observe(root.querySelector('.ls-header__inner'));
    headers.set(root, {
      resize: onModeChange,
      destroy: function () {
        events.abort();
        cancelFocusTransfer(true);
        if (resizeObserver) resizeObserver.disconnect();
        setMobileOpen(false, false);
        root.style.removeProperty('--ls-menu-max-height');
      }
    });
    setMobileOpen(false, false);
    var initialSide = navigationSide(document.activeElement);
    if (initialSide) lastNavigationFocus = { element: document.activeElement, side: initialSide };
  }

  function initializeFeedback(scope) {
    if (window.Shopify && window.Shopify.designMode) return;
    var candidates = Array.from(scope.querySelectorAll('.lsw-errors,.lsw-success,[data-ls-form-feedback]'))
      .filter(function (element) { return !focusedFeedback.has(element) && element.getClientRects().length; });
    if (!candidates.length) return;
    var anchor;
    try { anchor = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch (error) { anchor = null; }
    var anchored = anchor && candidates.find(function (element) { return anchor === element || anchor.contains(element); });
    var feedback = anchored || candidates.find(function (element) { return element.getAttribute('role') === 'alert'; }) || candidates[0];
    // Mark this response set together: do not move focus through several forms on one page.
    candidates.forEach(function (element) { focusedFeedback.add(element); });
    feedback.setAttribute('tabindex', '-1');
    window.requestAnimationFrame(function () {
      if (!feedback.isConnected) return;
      focusElement(feedback);
      feedback.scrollIntoView({ block: 'center', behavior: 'auto' });
    });
  }

  function initialize(scope) {
    headers.forEach(function (state, root) { if (!root.isConnected) { state.destroy(); headers.delete(root); } });
    scope.querySelectorAll('.ls-header[data-ls-navigation="disclosure"]').forEach(initializeHeader);
    initializeFeedback(scope);
    queueMeasurement();
  }
  function start() { initialize(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: globalEvents.signal });
  else start();
  document.addEventListener('shopify:section:load', function (event) { initialize(event.target); }, { signal: globalEvents.signal });
  document.addEventListener('shopify:section:unload', function (event) {
    headers.forEach(function (state, root) { if (event.target.contains(root)) { state.destroy(); headers.delete(root); } });
    queueMeasurement();
  }, { signal: globalEvents.signal });
  window.addEventListener('resize', function () { headers.forEach(function (state) { state.resize(); }); queueMeasurement(); }, { passive: true, signal: globalEvents.signal });
  window.addEventListener('scroll', queueMeasurement, { passive: true, signal: globalEvents.signal });
  window.addEventListener('pageshow', function () { headers.forEach(function (state) { state.resize(); }); queueMeasurement(); }, { signal: globalEvents.signal });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (!globalEvents.signal.aborted) queueMeasurement(); });
  window.lettermanRuntimeAccessibility = {
    destroy: function () {
      globalEvents.abort();
      headers.forEach(function (state) { state.destroy(); });
      headers.clear();
      if (pendingFrame) window.cancelAnimationFrame(pendingFrame);
      pendingFrame = 0;
      document.documentElement.style.removeProperty('--ls-sticky-header-height');
    }
  };
})();
